import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Library,
  Users,
  Sparkles,
  Bookmark,
} from "lucide-react";
import SiteNavbar from "../components/SiteNavbar";
import SiteFooter from "../components/SiteFooter";
import ShelfCard from "../components/ShelfCard";
import {
  CURATED_SHELVES,
  SHELVES_PILLARS,
  SHELVES_HOW_IT_WORKS,
  SHELVES_FAQ,
  SHELVES_STATS,
  SHELF_MOODS,
} from "../data/shelvesContent";

const ease = [0.21, 0.47, 0.32, 0.98];

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.75, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const PILLAR_ICONS = [Library, Users, Sparkles];

const ShelvesPage = () => {
  const [activeMood, setActiveMood] = useState("All moods");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredShelves =
    activeMood === "All moods"
      ? CURATED_SHELVES
      : CURATED_SHELVES.filter((s) => s.moods?.includes(activeMood));

  return (
    <div
      data-testid="shelves-page"
      className="min-h-screen paper-overlay"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <SiteNavbar />

      <main className="pt-32 md:pt-40 pb-28">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease }}
            className="text-center max-w-3xl mx-auto"
          >
            <span
              className="inline-block text-[11px] uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--brand)" }}
            >
              Discover new realms
            </span>
            <h1
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] mb-6"
              style={{ color: "var(--text)" }}
            >
              Immerse yourself in{" "}
              <span className="italic" style={{ color: "var(--brand)" }}>
                curated shelves
              </span>
            </h1>
            <p
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: "var(--text-soft)" }}
            >
              PagePeers gathers editorial collections, member shelves, and
              circle picks into themed reading rooms — each with a story, a
              curator, and books chosen for a specific mood.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.2 }}
            className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
          >
            {SHELVES_STATS.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl p-6 text-center cozy-card"
                style={{ background: "var(--surface)" }}
              >
                <p
                  className="font-serif text-3xl md:text-4xl font-medium mb-1"
                  style={{ color: "var(--brand)" }}
                >
                  {stat.value}
                </p>
                <p className="text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </motion.div>

          {/* Pillars */}
          <div className="mt-20 md:mt-28 grid md:grid-cols-3 gap-6">
            {SHELVES_PILLARS.map((pillar, i) => {
              const Icon = PILLAR_ICONS[i] || Library;
              return (
                <FadeIn key={pillar.title} delay={i * 0.08}>
                  <div
                    className="rounded-3xl p-8 h-full"
                    style={{
                      background: "var(--bg-alt)",
                      border: "1px solid var(--border-soft)",
                    }}
                  >
                    <Icon
                      className="w-6 h-6 mb-5"
                      style={{ color: "var(--brand)" }}
                    />
                    <h2
                      className="font-serif text-xl font-medium mb-3"
                      style={{ color: "var(--text)" }}
                    >
                      {pillar.title}
                    </h2>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: "var(--text-soft)" }}
                    >
                      {pillar.desc}
                    </p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>

        {/* Featured shelves */}
        <section className="mt-24 md:mt-32">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <FadeIn>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                <div>
                  <h2
                    className="font-serif text-3xl md:text-4xl font-medium tracking-tight"
                    style={{ color: "var(--text)" }}
                  >
                    Featured collections
                  </h2>
                  <p
                    className="mt-3 text-base max-w-xl"
                    style={{ color: "var(--text-soft)" }}
                  >
                    Six shelves to start with — each opens into a full reading
                    guide with images, curator notes, and save-to-library links.
                  </p>
                </div>
              </div>
            </FadeIn>

            {/* Mood filter */}
            <div className="flex flex-wrap gap-2 mb-10">
              {SHELF_MOODS.map((mood) => (
                <button
                  key={mood}
                  type="button"
                  onClick={() => setActiveMood(mood)}
                  className="text-xs md:text-sm px-4 py-2 rounded-full border transition-all"
                  style={{
                    borderColor:
                      activeMood === mood ? "var(--brand)" : "var(--border)",
                    background:
                      activeMood === mood ? "var(--brand-tint)" : "var(--surface)",
                    color: activeMood === mood ? "var(--brand)" : "var(--text-soft)",
                    fontWeight: activeMood === mood ? 600 : 400,
                  }}
                >
                  {mood}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredShelves.map((shelf, i) => (
                <FadeIn key={shelf.slug} delay={i * 0.05}>
                  <ShelfCard shelf={shelf} testId={`shelves-page-card-${i}`} />
                </FadeIn>
              ))}
            </div>

            {filteredShelves.length === 0 && (
              <p
                className="text-center py-12 text-sm"
                style={{ color: "var(--text-muted)" }}
              >
                No shelves match this mood yet — try another filter.
              </p>
            )}
          </div>
        </section>

        {/* How shelves work */}
        <section
          className="mt-24 md:mt-32 py-20 md:py-28"
          style={{ background: "var(--bg-alt)" }}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <FadeIn>
              <h2
                className="font-serif text-3xl md:text-4xl font-medium text-center mb-4"
                style={{ color: "var(--text)" }}
              >
                How curated shelves work
              </h2>
              <p
                className="text-center max-w-xl mx-auto mb-16 text-base"
                style={{ color: "var(--text-soft)" }}
              >
                From discovery to your personal library in three calm steps.
              </p>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-8 md:gap-10">
              {SHELVES_HOW_IT_WORKS.map((item, i) => (
                <FadeIn key={item.step} delay={i * 0.1}>
                  <div className="text-center md:text-left">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto md:mx-0 mb-5 cozy-card"
                      style={{ background: "var(--surface)" }}
                    >
                      <span
                        className="font-serif text-2xl italic font-medium"
                        style={{ color: "var(--brand)" }}
                      >
                        {item.step}
                      </span>
                    </div>
                    <h3
                      className="font-serif text-xl font-medium mb-3"
                      style={{ color: "var(--text)" }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: "var(--text-soft)" }}
                    >
                      {item.body}
                    </p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* Build your own */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-24 md:mt-32">
          <FadeIn>
            <div
              className="rounded-3xl p-10 md:p-14 grid lg:grid-cols-2 gap-10 items-center"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                boxShadow: "var(--card-shadow)",
              }}
            >
              <div>
                <Bookmark
                  className="w-8 h-8 mb-6"
                  style={{ color: "var(--brand)" }}
                />
                <h2
                  className="font-serif text-3xl md:text-4xl font-medium mb-4"
                  style={{ color: "var(--text)" }}
                >
                  Build your own shelf
                </h2>
                <p
                  className="text-base leading-relaxed mb-6"
                  style={{ color: "var(--text-soft)" }}
                >
                  You are not limited to our featured collections. Create a
                  public shelf for your book club, a private list for gifts, or
                  a themed room for your favorite genre — then share the link
                  with anyone.
                </p>
                <ul className="space-y-2 text-sm" style={{ color: "var(--text-soft)" }}>
                  <li>· Drag-and-drop book order</li>
                  <li>· Public, unlisted, or private visibility</li>
                  <li>· Optional curator note at the top</li>
                  <li>· Connect shelves to reader circles</li>
                </ul>
              </div>
              <div
                className="rounded-2xl p-8 md:p-10"
                style={{ background: "var(--bg-alt)" }}
              >
                <p
                  className="font-serif text-2xl italic leading-snug mb-6"
                  style={{ color: "var(--text)" }}
                >
                  “My shelf is the reading map I wish bookstores had — moods,
                  not marketing categories.”
                </p>
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  — Priya R., PagePeers member
                </p>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 mt-24 md:mt-32">
          <FadeIn>
            <h2
              className="font-serif text-3xl md:text-4xl font-medium text-center mb-12"
              style={{ color: "var(--text)" }}
            >
              Shelf questions
            </h2>
          </FadeIn>
          <div className="space-y-4">
            {SHELVES_FAQ.map((item, i) => (
              <FadeIn key={item.q} delay={i * 0.06}>
                <div
                  className="rounded-2xl p-6 md:p-8 cozy-card"
                  style={{ background: "var(--surface)" }}
                >
                  <h3
                    className="font-serif text-lg md:text-xl font-medium mb-3"
                    style={{ color: "var(--text)" }}
                  >
                    {item.q}
                  </h3>
                  <p
                    className="text-sm md:text-base leading-relaxed"
                    style={{ color: "var(--text-soft)" }}
                  >
                    {item.a}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-24 md:mt-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="rounded-3xl p-10 md:p-14 text-center"
            style={{
              background:
                "linear-gradient(135deg, var(--brand-tint) 0%, var(--bg-alt) 100%)",
              border: "1px solid var(--border)",
            }}
          >
            <h2
              className="font-serif text-3xl md:text-4xl font-medium mb-4"
              style={{ color: "var(--text)" }}
            >
              Start with a shelf today
            </h2>
            <p
              className="text-base mb-8 max-w-lg mx-auto"
              style={{ color: "var(--text-soft)" }}
            >
              Pick a collection above or create your own — your next favorite
              book is one curated room away.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://club.pagepeers.com/register"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-transform hover:scale-[1.02]"
                style={{
                  background: "var(--brand)",
                  color: "var(--on-brand)",
                  boxShadow: "0 6px 20px -6px rgba(194,90,55,0.5)",
                }}
              >
                Join the Club
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm border transition-colors"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--text)",
                  background: "var(--surface)",
                }}
              >
                How it works
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default ShelvesPage;
